/**
 * ViewportOrientationContext — coach-app vs learner-app field orientation.
 *
 * Background
 *   The football field is 100 yd × 53.3 yd. Coaches author plays in the
 *   **playbook** convention: end zone at the top, offense moving up the
 *   page (portrait). Learners watch in the **broadcast** convention:
 *   sideline view, offense moving across the screen (landscape).
 *
 *   We render from a single shared codebase under `core/ui/learner-app/src/`
 *   that coach-app re-uses via the `@scenario-os/...` Vite alias, so the
 *   per-role default has to be pushed in by the *shell*, not branched on
 *   `useAuth().isCoach` inside the renderer (which couples view code to
 *   auth, breaks "preview as learner", and leaks across apps).
 *
 * Sources of truth (in priority order)
 *   1. An explicit `orientationOverride` prop on the renderer (used by the
 *      Scene Designer's "view as learner" preview toggle so the coach can
 *      sanity-check the landscape framing without leaving the designer).
 *   2. The user's persisted preference in localStorage
 *      (`scenario_os_scene_orientation`). Lets a learner force portrait
 *      on a phone, or a power-user coach flip to landscape permanently.
 *   3. The shell default — `defaultOrientation` on the provider, set by
 *      coach-app to 'portrait' and learner-app to 'landscape', read from
 *      `process.env.REACT_APP_SCENE_ORIENTATION_DEFAULT` (Vite `define`).
 *   4. Hard fallback: 'landscape' (matches the legacy hardcoded behaviour
 *      so any non-football pack mounted before this provider keeps working).
 *
 * Coordinate model is unchanged — actor positions stay in field yards
 * (x=0..100 downfield, y=0..53.3 across). Orientation is purely a render-
 * time decision; no migration of `scenes`, `formations`, or `plays`.
 */

import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';

const STORAGE_KEY = 'scenario_os_scene_orientation';
const VALID = new Set(['landscape', 'portrait']);

const HARD_FALLBACK = 'landscape';

function readEnvDefault() {
  // CRA-shim env (vite.config.js `define`). Both apps inline this at
  // build time. If unset, we fall back to landscape so the legacy
  // behaviour stands.
  try {
    const v = (process.env.REACT_APP_SCENE_ORIENTATION_DEFAULT || '').trim().toLowerCase();
    if (VALID.has(v)) return v;
  } catch (_) { /* SSR / test */ }
  return HARD_FALLBACK;
}

function readStoredOverride() {
  try {
    const v = (localStorage.getItem(STORAGE_KEY) || '').trim().toLowerCase();
    if (VALID.has(v)) return v;
  } catch (_) { /* private mode / SSR */ }
  return null;
}

const ViewportOrientationContext = createContext(null);

export function ViewportOrientationProvider({ children, defaultOrientation }) {
  const fallback = VALID.has(defaultOrientation) ? defaultOrientation : readEnvDefault();
  const [override, setOverride] = useState(() => readStoredOverride());

  const orientation = override || fallback;

  const persist = useCallback((next) => {
    if (next === null || next === undefined) {
      try { localStorage.removeItem(STORAGE_KEY); } catch (_) { /* noop */ }
      setOverride(null);
      return;
    }
    if (!VALID.has(next)) return;
    try { localStorage.setItem(STORAGE_KEY, next); } catch (_) { /* noop */ }
    setOverride(next);
  }, []);

  // Keep multi-tab installs in sync — if the coach has the designer open
  // in one tab and the learner sim in another, flipping in one tab
  // shouldn't leave the other stuck on the old setting.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== STORAGE_KEY) return;
      const next = (e.newValue || '').trim().toLowerCase();
      setOverride(VALID.has(next) ? next : null);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const value = useMemo(() => ({
    orientation,
    defaultOrientation: fallback,
    isOverridden: override !== null,
    setOrientation: persist,
    resetOrientation: () => persist(null),
    toggleOrientation: () => persist(orientation === 'portrait' ? 'landscape' : 'portrait'),
  }), [orientation, fallback, override, persist]);

  return (
    <ViewportOrientationContext.Provider value={value}>
      {children}
    </ViewportOrientationContext.Provider>
  );
}

/**
 * Read the resolved orientation. Safe to call without a provider — falls
 * back to the env default (and ultimately 'landscape') so renderers
 * mounted in unit tests / Storybook don't crash.
 */
export function useViewportOrientation() {
  const ctx = useContext(ViewportOrientationContext);
  if (ctx) return ctx;
  const fallback = readEnvDefault();
  return {
    orientation: fallback,
    defaultOrientation: fallback,
    isOverridden: false,
    setOrientation: () => {},
    resetOrientation: () => {},
    toggleOrientation: () => {},
  };
}

/**
 * Resolve orientation for a renderer mount. Renderers should call this
 * once (RenderRouter does it for the football pipeline) so an explicit
 * `orientationOverride` prop trumps the user preference / shell default
 * — the Scene Designer's "view as learner" preview relies on this.
 */
export function resolveOrientation(override, contextValue) {
  if (VALID.has(override)) return override;
  if (contextValue && VALID.has(contextValue.orientation)) return contextValue.orientation;
  return readEnvDefault();
}

export const ORIENTATION_VALUES = Array.from(VALID);
